import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Typography } from '@mui/material';
import { DateTime } from 'luxon';
import { getDescriptionForCollectorStatus } from '../types/CollectorStatus';
import { getStatusData } from '../../../services/order-service';
import { RightPointingArrow } from '../../../components/RightPointingArrow';
import { WaitingImpatientlyIcon } from '../../../components/WaitingImpatientlyIcon';
import { FlyingTheDroneCloud } from '../../../components/FlyingTheDroneCloud';
import { DeliveredCloud } from '../../../components/DeliveredCloud';
import { TakingOffCloud } from '../../../components/TakingOffCloud';
import { NewOrderIcon } from '../../../components/NewOrderIcon';
import CheckIcon from '@mui/icons-material/Check';
import NearMeIcon from '@mui/icons-material/NearMe';
import { useAppDispatch, useAppSelector } from '../../../hooks/redux';
import { fetchLatestExpectedOrderTimeByOrderId } from '../../../actions/orderActions';
import { ExpectedOrderTime } from '@m4a3/api-client';

// Expected times data structure (camelCase to match the API client model).
// We intentionally pick only the fields this component cares about.
export type ExpectedTimes = Pick<
  ExpectedOrderTime,
  | 'id'
  | 'orderId'
  | 'expectedDeliveryDate'
  | 'expectedTakeoffDate'
  | 'expectedDropoffDate'
  | 'expectedPickupDate'
  | 'expectedPreparingDate'
  | 'expectedLeaveDate'
>;

type ExpectedTimeKey = Exclude<keyof ExpectedTimes, 'id' | 'orderId'>;

// Order history item structure
export interface OrderHistoryItem {
  id?: string;
  timestamp?: string;
  expectedTimestamp?: string | null;
  lateByMins?: number | null;
  orderId?: string;
  createDate?: string;
  createdAt?: string;
  time?: string;
  status?: string;
  collectorStatus?: string;
  changeType?: 'ORDER' | 'COLLECTOR' | 'WARNING';
}

interface ExpectedTimetableProps {
  expectedTimes: ExpectedTimes;
  orderHistory: OrderHistoryItem[];
  timezone?: string;
  collectorName?: string;
}

export type ExpectedTimetableConnectedProps = {
  orderId: string;
  orderHistory: OrderHistoryItem[];
  timezone?: string;
  /**
   * Optional fallback expected times (e.g. demo / SSR / previously loaded)
   * which will be used until the latest expected times load.
   */
  initialExpectedTimes?: ExpectedTimes;
};

// Expected time label mapping - all use blue color for dotted lines
const EXPECTED_TIME_LABELS: Record<ExpectedTimeKey, { label: string }> = {
  expectedPreparingDate: { label: 'Preparing' },
  expectedLeaveDate: { label: 'Leave' },
  expectedPickupDate: { label: 'Pickup' },
  expectedDropoffDate: { label: 'Dropoff' },
  expectedTakeoffDate: { label: 'Takeoff' },
  expectedDeliveryDate: { label: 'Delivery' },
};

// Blue color for all dotted lines
const DOTTED_LINE_COLOR = '#3B82F6';
const EXPECTED_NEUTRAL_LINE_COLOR = '#CBD5E1';

// Grid configuration
// Vertical density tuning
// Controls vertical compression of the whole timeline.
// Example: 2 minutes per row means the timeline is ~50% as tall.
const GRID_ROW_HEIGHT_PX = 22;
// Allow fractional minutes-per-row so short timelines can scale *up* to fill height.
// (e.g. 0.6 means each minute takes more vertical space)
const MINUTES_PER_ROW_MIN = 0.5;
const MINUTES_PER_ROW_MAX = 6;
const HEADER_HEIGHT_PX = 34;
const COLUMN_GAP_PX = 16;
const COLUMN_INNER_PADDING_PX = 10;
const TIME_PADDING_MINUTES = 2;

// Mapping of status events to their expected time keys
const STATUS_TO_EXPECTED_TIME: Record<string, ExpectedTimeKey> = {
  // Order statuses
  'STORE_PREPARING': 'expectedPreparingDate',
  'STORE_PREPARED': 'expectedPickupDate',
  'RECEIVED_FROM_STORE': 'expectedDropoffDate',
  'DISPATCHED': 'expectedTakeoffDate',
  'DELIVERED': 'expectedDeliveryDate',
  // Collector statuses
  'EN_ROUTE_TO_MERCHANT': 'expectedLeaveDate',
  'COLLECTOR_NEAR_MERCHANT': 'expectedPickupDate',
  'WAITING_AT_MERCHANT': 'expectedPickupDate',
  'EN_ROUTE_TO_MANNA_BASE': 'expectedPickupDate',
  'COLLECTOR_NEAR_LZ': 'expectedDropoffDate',
  'AT_MANNA_BASE': 'expectedDropoffDate',
  'HANDED_OVER': 'expectedDropoffDate',
};

const getTimestamp = (item: OrderHistoryItem): string | null => {
  return item?.timestamp || item?.createDate || item?.createdAt || item?.time || null;
};

const isWarningHistoryItem = (item: OrderHistoryItem): boolean => {
  return item?.changeType === 'WARNING';
};

// Get icon for order status
const getIconForOrderStatus = (status: string): JSX.Element => {
  switch (status) {
    case 'CREATED':
    case 'NEW':
      return <NewOrderIcon size={18} iconColor="#14142B" backgroundColor="transparent" />;
    case 'ACCEPTED':
    case 'STORE_ACCEPTED':
      return <CheckIcon style={{ color: '#14142B', fontSize: 18 }} />;
    case 'STORE_PREPARING':
      return <WaitingImpatientlyIcon size={18} iconColor="#14142B" backgroundColor="transparent" />;
    case 'STORE_PREPARED':
      return <CheckIcon style={{ color: '#14142B', fontSize: 18 }} />;
    case 'RECEIVED_FROM_STORE':
      return <RightPointingArrow size={18} arrowColor="#14142B" backgroundColor="transparent" strokeWidth={2} />;
    case 'READY':
      return <TakingOffCloud size={18} iconColor="#14142B" backgroundColor="transparent" />;
    case 'DISPATCHED':
      return <FlyingTheDroneCloud size={18} iconColor="#14142B" backgroundColor="transparent" />;
    case 'DELIVERED':
      return <DeliveredCloud size={18} />;
    case 'COMPLETE':
      return <CheckIcon style={{ color: '#14142B', fontSize: 18 }} />;
    default:
      return <RightPointingArrow size={18} arrowColor="#14142B" backgroundColor="transparent" strokeWidth={2} />;
  }
};

// Get icon for collector status
const getIconForCollectorStatus = (status: string): JSX.Element => {
  switch (status) {
    case 'WAITING_FOR_COLLECTOR_TO_ACCEPT':
      return <WaitingImpatientlyIcon size={18} iconColor="#14142B" backgroundColor="transparent" />;
    case 'COLLECTOR_ASSIGNED':
      return <CheckIcon style={{ color: '#14142B', fontSize: 18 }} />;
    case 'EN_ROUTE_TO_MERCHANT':
      return <RightPointingArrow size={18} arrowColor="#14142B" backgroundColor="transparent" strokeWidth={2} />;
    case 'COLLECTOR_NEAR_MERCHANT':
      return <NearMeIcon style={{ color: '#14142B', fontSize: 18 }} />;
    case 'WAITING_AT_MERCHANT':
      return <WaitingImpatientlyIcon size={18} iconColor="#14142B" backgroundColor="transparent" />;
    case 'EN_ROUTE_TO_MANNA_BASE':
      return <RightPointingArrow size={18} arrowColor="#14142B" backgroundColor="transparent" strokeWidth={2} />;
    case 'COLLECTOR_NEAR_LZ':
      return <NearMeIcon style={{ color: '#14142B', fontSize: 18 }} />;
    case 'AT_MANNA_BASE':
      return <CheckIcon style={{ color: '#14142B', fontSize: 18 }} />;
    case 'HANDED_OVER':
      return <CheckIcon style={{ color: '#14142B', fontSize: 18 }} />;
    default:
      return <RightPointingArrow size={18} arrowColor="#14142B" backgroundColor="transparent" strokeWidth={2} />;
  }
};

const parseDateTime = (dateStr: string, timezone: string): DateTime => {
  // Handle both ISO format and space-separated format (e.g., "2025-12-11 14:13:00")
  const normalized = dateStr.replace(' ', 'T');
  if (normalized.includes('T') && !normalized.includes('Z') && !normalized.includes('+')) {
    // Local time without timezone - parse as local
    return DateTime.fromISO(normalized, { zone: timezone });
  }
  return DateTime.fromISO(normalized, { zone: 'utc' }).setZone(timezone);
};

const formatTime = (dateStr: string, timezone: string): string => {
  const dt = parseDateTime(dateStr, timezone);
  return dt.toFormat('HH:mm');
};

export const ExpectedTimetable: React.FC<ExpectedTimetableProps> = ({
  expectedTimes,
  orderHistory,
  timezone = 'Europe/Dublin',
  collectorName,
}) => {
  const [hoveredEventId, setHoveredEventId] = useState<string | null>(null);
  const [hoveredExpectedKey, setHoveredExpectedKey] = useState<string | null>(null);
  const [hoverSource, setHoverSource] = useState<'event' | 'expected' | null>(null);
  const scrollViewportRef = useRef<HTMLDivElement | null>(null);
  const [scrollViewportHeightPx, setScrollViewportHeightPx] = useState<number | null>(null);

  const collectorDisplayName = useMemo(() => {
    const trimmed = collectorName?.trim();
    return trimmed ? trimmed : 'collector';
  }, [collectorName]);

  // Extract all expected times as sorted array
  const sortedExpectedTimes = useMemo(() => {
    const times: { key: ExpectedTimeKey; date: DateTime; label: string }[] = [];
    
    Object.entries(EXPECTED_TIME_LABELS).forEach(([key, { label }]) => {
      const dateStr = expectedTimes[key as ExpectedTimeKey];
      if (dateStr && typeof dateStr === 'string') {
        times.push({
          key: key as ExpectedTimeKey,
          date: parseDateTime(dateStr, timezone),
          label,
        });
      }
    });
    
    return times.sort((a, b) => a.date.toMillis() - b.date.toMillis());
  }, [expectedTimes, timezone]);

  // Calculate time range for the grid
  const { minTime, maxTime, totalMinutes } = useMemo(() => {
    if (sortedExpectedTimes.length === 0) {
      return { minTime: DateTime.now(), maxTime: DateTime.now(), totalMinutes: 60 };
    }
    
    const allTimes = [...sortedExpectedTimes.map(t => t.date)];
    
    // Add order history times
    orderHistory.forEach(item => {
      if (isWarningHistoryItem(item)) return;
      const ts = getTimestamp(item);
      if (ts) {
        allTimes.push(parseDateTime(ts, timezone));
      }
    });
    
    // Find CREATED event to use as the start time
    const createdEvent = orderHistory.find(
      (item) =>
        !isWarningHistoryItem(item) &&
        (item.status === 'CREATED' || item.status === 'NEW')
    );
    
    let earliest: DateTime;
    if (createdEvent) {
      const createdTs = getTimestamp(createdEvent);
      earliest = createdTs ? parseDateTime(createdTs, timezone).startOf('minute') : DateTime.min(...allTimes).startOf('minute');
    } else {
      earliest = DateTime.min(...allTimes).startOf('minute');
    }

    // Start/end padding so the first/last entries aren't pinned to the edges.
    const min = earliest.minus({ minutes: TIME_PADDING_MINUTES });
    const latest = DateTime.max(...allTimes).startOf('minute');
    // End the grid at the last known timestamp (expected time or status entry).
    // Keep a tiny minimum so the UI doesn't collapse when min === max.
    const max = latest.plus({ minutes: TIME_PADDING_MINUTES });
    const total = Math.ceil(max.diff(min, 'minutes').minutes);

    return { minTime: min, maxTime: max, totalMinutes: Math.max(total, 1) };
  }, [sortedExpectedTimes, orderHistory, timezone]);

  // Observe the scroll viewport height so we can dynamically compress the Y axis.
  useEffect(() => {
    const el = scrollViewportRef.current;
    if (!el) return;

    // Initial measure (ResizeObserver callback can be async).
    setScrollViewportHeightPx(el.getBoundingClientRect().height);

    const ro = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;
      setScrollViewportHeightPx(entry.contentRect.height);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const minutesPerRow = useMemo(() => {
    // Fallback to the previous "nice" density if we haven't measured yet.
    const viewportH = scrollViewportHeightPx ?? 520;
    // The grid lives inside the scroll viewport alongside a sticky header.
    const usable = Math.max(240, viewportH - HEADER_HEIGHT_PX);
    // Choose a scale so the grid *fills* the visible height when possible.
    // If the timeline is long, minutesPerRow grows (compression) and the grid scrolls.
    const raw = (totalMinutes * GRID_ROW_HEIGHT_PX) / usable;
    const clamped = Math.max(MINUTES_PER_ROW_MIN, Math.min(MINUTES_PER_ROW_MAX, raw || 1));
    return clamped;
  }, [scrollViewportHeightPx, totalMinutes]);

  // Calculate pixel position for a given time (not row-based, direct pixel mapping)
  const getPixelPositionForTime = (dt: DateTime): number => {
    const minutesFromStart = dt.diff(minTime, 'minutes').minutes;
    return (minutesFromStart / minutesPerRow) * GRID_ROW_HEIGHT_PX;
  };

  // Process order history events
  const historyEvents = useMemo(() => {
    return orderHistory
      .filter(item => {
        if (isWarningHistoryItem(item)) return false;
        const ts = getTimestamp(item);
        // In actual data shape, `status` is always present (collector + order),
        // and `changeType` disambiguates.
        return ts && !!item.status;
      })
      .map((item, idx) => {
        const ts = getTimestamp(item)!;
        const dt = parseDateTime(ts, timezone);
        const isCollector = item.changeType === 'COLLECTOR' || !!item.collectorStatus;
        const statusKey = item.status || item.collectorStatus || '';
        
        let label = '';
        
        if (isCollector) {
          // Collector events are encoded in `status` (per actual payload shape).
          label = getDescriptionForCollectorStatus(statusKey, collectorDisplayName);
        } else {
          const statusData = getStatusData(item.status || '');
          label = statusData?.text || item.status || 'Unknown';
        }
        
        // Find the expected time for this status
        const expectedTimeKey = STATUS_TO_EXPECTED_TIME[statusKey];
        let expectedDateTime: DateTime | null = null;
        let diffMinutes: number | null = null;
        let expectedPixelPos: number | null = null;
        
        if (expectedTimeKey && expectedTimes[expectedTimeKey]) {
          expectedDateTime = parseDateTime(expectedTimes[expectedTimeKey] as string, timezone);
          diffMinutes = Math.round(dt.diff(expectedDateTime, 'minutes').minutes);
          expectedPixelPos = getPixelPositionForTime(expectedDateTime);
        }
        
        const baseId =
          item.id ||
          `${item.changeType || (isCollector ? 'COLLECTOR' : 'ORDER')}-${statusKey}-${ts}`;

        return {
          // Ensure uniqueness even if upstream data reuses IDs (demo data does).
          id: `${baseId}-${idx}`,
          timestamp: ts,
          datetime: dt,
          isCollector,
          status: statusKey,
          label,
          expectedTimeKey: expectedTimeKey || null,
          pixelPos: getPixelPositionForTime(dt),
          expectedDateTime,
          expectedPixelPos,
          diffMinutes,
        };
      })
      .sort((a, b) => a.datetime.toMillis() - b.datetime.toMillis());
  }, [orderHistory, timezone, minTime, expectedTimes, minutesPerRow, collectorDisplayName]);

  const BOX_HEIGHT_PX = 40;
  const STACK_GAP_PX = 8;
  const TIME_COLUMN_WIDTH_PX = 120;

  // Grid height based on total minutes.
  // (Compression is handled via minutesPerRow in getPixelPositionForTime.)
  // Use the exact (non-ceiled) height so we don't introduce a tiny overflow scrollbar
  // from rounding up to a full row.
  const gridHeight = Math.max(1, (totalMinutes / minutesPerRow) * GRID_ROW_HEIGHT_PX);

  const onExpectedHoverEnter = (key: string) => {
    setHoverSource('expected');
    setHoveredEventId(null);
    setHoveredExpectedKey(key);
  };

  const onExpectedHoverLeave = () => {
    setHoverSource(null);
    setHoveredEventId(null);
    setHoveredExpectedKey(null);
  };

  // Lay out events per column so cards never overlap vertically, even when times are close
  // (e.g. 1 minute apart at higher density).
  const eventBoxTopById = useMemo(() => {
    const layoutColumn = (events: typeof historyEvents) => {
      const ordered = [...events].sort((a, b) => a.pixelPos - b.pixelPos);
      const tops: number[] = [];

      for (let i = 0; i < ordered.length; i++) {
        const ev = ordered[i]!;
        const desiredTop = ev.pixelPos - BOX_HEIGHT_PX / 2;
        const prevTop = i > 0 ? tops[i - 1]! : -Infinity;
        const minTop = i > 0 ? prevTop + BOX_HEIGHT_PX + STACK_GAP_PX : desiredTop;
        tops[i] = Math.max(desiredTop, minTop);
      }

      // If we overflow the grid, shift the whole column up.
      const lastBottom = tops.length ? tops[tops.length - 1]! + BOX_HEIGHT_PX : 0;
      let shift = 0;
      if (lastBottom > gridHeight) shift = lastBottom - gridHeight;
      if (shift !== 0) {
        for (let i = 0; i < tops.length; i++) tops[i] = tops[i]! - shift;
      }

      // If shifting made the first one negative, shift down.
      if (tops.length && tops[0]! < 0) {
        const down = -tops[0]!;
        for (let i = 0; i < tops.length; i++) tops[i] = tops[i]! + down;
      }

      // Final pass to guarantee no overlaps after shifts.
      for (let i = 1; i < tops.length; i++) {
        const minTop = tops[i - 1]! + BOX_HEIGHT_PX + STACK_GAP_PX;
        if (tops[i]! < minTop) tops[i] = minTop;
      }

      const byId: Record<string, number> = {};
      ordered.forEach((ev, i) => {
        byId[ev.id] = Math.max(0, Math.min(tops[i]!, gridHeight - BOX_HEIGHT_PX));
      });
      return byId;
    };

    return {
      ...layoutColumn(historyEvents.filter((e) => !e.isCollector)),
      ...layoutColumn(historyEvents.filter((e) => e.isCollector)),
    };
  }, [historyEvents, gridHeight]);

  // For each expected time, find the *latest* associated event and use its diffMinutes
  // for the pill shown next to the expected time label.
  const latestDiffMinutesByExpectedKey = useMemo(() => {
    const latestByKey = new Map<ExpectedTimeKey, { datetimeMs: number; diffMinutes: number }>();

    historyEvents.forEach((event) => {
      const key = event.expectedTimeKey;
      if (!key || event.diffMinutes === null) return;

      const ms = event.datetime.toMillis();
      const existing = latestByKey.get(key);
      if (!existing || ms > existing.datetimeMs) {
        latestByKey.set(key, { datetimeMs: ms, diffMinutes: event.diffMinutes });
      }
    });

    // If NOW has passed an expected time but no associated statuses have happened yet,
    // show a "Late by X mins" pill driven by current time.
    const now = DateTime.now().setZone(timezone).startOf('minute');

    const out: Record<ExpectedTimeKey, number | null> = {} as any;
    (Object.keys(EXPECTED_TIME_LABELS) as ExpectedTimeKey[]).forEach((k) => {
      const fromEvent = latestByKey.get(k)?.diffMinutes;
      if (typeof fromEvent === 'number') {
        out[k] = fromEvent;
        return;
      }

      const dateStr = expectedTimes[k];
      if (dateStr && typeof dateStr === 'string') {
        const expectedDt = parseDateTime(dateStr, timezone).startOf('minute');
        const minsLate = Math.round(now.diff(expectedDt, 'minutes').minutes);
        out[k] = minsLate > 0 ? minsLate : null;
        return;
      }

      out[k] = null;
    });

    return out;
  }, [historyEvents, expectedTimes, timezone]);

  const EXPECTED_BOX_HEIGHT_PX = 40;
  const EXPECTED_BOX_GAP_PX = 6;
  const EXPECTED_NEAR_THRESHOLD_PX = GRID_ROW_HEIGHT_PX * 2;

  // Generate time markers for the grid (every 5 minutes)
  const timeMarkers = useMemo(() => {
    const markers: { time: string; pixelPos: number }[] = [];
    let current = minTime;
    
    while (current <= maxTime) {
      markers.push({
        time: current.toFormat('HH:mm'),
        pixelPos: getPixelPositionForTime(current),
      });
      current = current.plus({ minutes: 5 });
    }
    
    return markers;
  }, [minTime, maxTime, minutesPerRow]);

  const expectedLabelTopByKey = useMemo(() => {
    // Prevent expected-time boxes in the time column from overlapping when
    // expected times are equal or very close.
    const items = sortedExpectedTimes
      .map(({ key, date }) => ({
        key,
        desiredCenter: getPixelPositionForTime(date),
      }))
      .sort((a, b) => a.desiredCenter - b.desiredCenter);

    const byKey: Record<string, number> = {};
    const groups: Array<typeof items> = [];
    let current: typeof items = [];

    items.forEach((item) => {
      if (current.length === 0) {
        current = [item];
        return;
      }

      const prev = current[current.length - 1]!;
      if (item.desiredCenter - prev.desiredCenter <= EXPECTED_NEAR_THRESHOLD_PX) {
        current.push(item);
      } else {
        groups.push(current);
        current = [item];
      }
    });
    if (current.length > 0) groups.push(current);

    const clampTop = (top: number) =>
      Math.max(0, Math.min(top, gridHeight - EXPECTED_BOX_HEIGHT_PX));

    groups.forEach((group) => {
      if (group.length === 1) {
        const only = group[0]!;
        byKey[only.key] = clampTop(only.desiredCenter - EXPECTED_BOX_HEIGHT_PX / 2);
        return;
      }

      const anchor =
        group.reduce((sum, g) => sum + g.desiredCenter, 0) / group.length;
      const step = EXPECTED_BOX_HEIGHT_PX + EXPECTED_BOX_GAP_PX;
      const centerIdx = (group.length - 1) / 2;

      group.forEach((g, idx) => {
        const offset = (idx - centerIdx) * step;
        byKey[g.key] = clampTop(anchor - EXPECTED_BOX_HEIGHT_PX / 2 + offset);
      });
    });

    // Second pass: ensure monotonic non-overlap from top to bottom.
    const ordered = items
      .map((it) => ({ key: it.key, top: byKey[it.key] ?? 0 }))
      .sort((a, b) => a.top - b.top);

    for (let i = 1; i < ordered.length; i++) {
      const prev = ordered[i - 1]!;
      const cur = ordered[i]!;
      const minTop = prev.top + EXPECTED_BOX_HEIGHT_PX + EXPECTED_BOX_GAP_PX;
      if (cur.top < minTop) cur.top = minTop;
    }

    ordered.forEach(({ key, top }) => {
      byKey[key] = clampTop(top);
    });

    return byKey;
  }, [sortedExpectedTimes, minTime, gridHeight, minutesPerRow]);

  const hiddenTimeMarkerTimes = useMemo(() => {
    // If an expected-time label sits on top of a time marker, hide the marker
    // to avoid double-labeling in the time column.
    const expectedTimesSet = new Set(sortedExpectedTimes.map(({ date }) => date.toFormat('HH:mm')));
    const hidden = new Set<string>();

    timeMarkers.forEach(({ time, pixelPos }) => {
      if (expectedTimesSet.has(time)) hidden.add(time);
    });

    return hidden;
  }, [sortedExpectedTimes, timeMarkers]);

  const shouldScroll = useMemo(() => {
    // Only show a scroll container if content actually overflows.
    // Using a small epsilon avoids "tiny scroll" from sub-pixel rounding.
    if (!scrollViewportHeightPx) return true;
    const contentHeight = HEADER_HEIGHT_PX + gridHeight;
    return contentHeight > scrollViewportHeightPx + 8;
  }, [scrollViewportHeightPx, gridHeight]);

  return (
    <div
      style={{
        border: '1px solid #e5e7eb',
        borderRadius: 12,
        padding: 16,
        background: 'white',
        overflow: 'hidden',
        // Allow the inner scroll viewport to "fill" when the parent constrains height.
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: 0,
        // If our parent uses flex layout, allow this component to stretch.
        flex: 1,
      }}
    >
      {/* Timetable Grid */}
      <div
        ref={scrollViewportRef}
        style={{
          position: 'relative',
          // Fill available height from the parent layout (flex container).
          overflowY: shouldScroll ? 'auto' : 'hidden',
          overflowX: 'hidden',
          border: '1px solid #f3f4f6',
          borderRadius: 8,
          flex: 1,
          minHeight: 0,
        }}
      >
        {/* Sticky column headers */}
        <div
          style={{
            position: 'sticky',
            top: 0,
            left: 0,
            right: 0,
            height: HEADER_HEIGHT_PX,
            zIndex: 60,
            background: '#fafafa',
            borderBottom: '1px solid #e5e7eb',
            display: 'flex',
            alignItems: 'center',
            fontSize: 11,
            fontWeight: 700,
            color: '#6b7280',
            letterSpacing: 0.2,
            userSelect: 'none',
          }}
        >
          <div
            style={{
              width: TIME_COLUMN_WIDTH_PX,
              height: '100%',
              borderRight: '1px solid #e5e7eb',
              display: 'flex',
              alignItems: 'center',
              padding: '0 10px',
              flexShrink: 0,
            }}
          >
            Expected times
          </div>
          <div
            style={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              height: '100%',
              paddingRight: COLUMN_INNER_PADDING_PX,
            }}
          >
            <div style={{ width: `calc(50% - ${COLUMN_GAP_PX / 2}px)`, paddingLeft: COLUMN_INNER_PADDING_PX }}>
              Order Status History
            </div>
            <div style={{ width: COLUMN_GAP_PX }} />
            <div style={{ width: `calc(50% - ${COLUMN_GAP_PX / 2}px)` }}>Collector Status History</div>
          </div>
        </div>

        {/* Scrollable content */}
        <div style={{ position: 'relative', height: gridHeight }}>
          {/* Time markers on the left */}
          <div
            style={{
              position: 'absolute',
              left: 0,
              top: 0,
              width: TIME_COLUMN_WIDTH_PX,
              height: '100%',
              borderRight: '1px solid #e5e7eb',
              background: '#fafafa',
              zIndex: 10,
            }}
          >
          {timeMarkers.map(({ time, pixelPos }) => (
            <div
              key={time}
              style={{
                position: 'absolute',
                top: pixelPos - 8,
                left: 4,
                fontSize: 10,
                color: '#9ca3af',
                fontFamily: 'monospace',
                display: hiddenTimeMarkerTimes.has(time) ? 'none' : undefined,
              }}
            >
              {time}
            </div>
          ))}

          {/* Expected time labels (neutral) + late/early pill */}
          {sortedExpectedTimes.map(({ key, date, label }) => {
            const pixelPos = getPixelPositionForTime(date);
            const timeStr = date.toFormat('HH:mm');
            const shouldDim = hoveredExpectedKey !== null && hoveredExpectedKey !== key;
            const diff = latestDiffMinutesByExpectedKey[key] ?? null;
            const isLate = diff !== null && diff > 0;
            const top = expectedLabelTopByKey[key] ?? (pixelPos - EXPECTED_BOX_HEIGHT_PX / 2);

            return (
              <div
                key={`expected-left-${key}`}
                style={{
                  position: 'absolute',
                  top,
                  left: 6,
                  right: 6,
                  background: 'white',
                  border: '1px solid #E5E7EB',
                  borderRadius: 10,
                  padding: '6px 8px',
                  boxShadow: '0 1px 2px rgba(0,0,0,0.06)',
                  opacity: shouldDim ? 0.18 : 1,
                  transition: 'opacity 140ms ease',
                  cursor: 'pointer',
                }}
                onMouseEnter={() => onExpectedHoverEnter(key)}
                onMouseLeave={onExpectedHoverLeave}
              >
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 8 }}>
                    <div
                      style={{
                        fontSize: 11,
                        fontWeight: 700,
                        color: '#374151',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        minWidth: 0,
                        flex: 1,
                      }}
                    >
                      {label}
                    </div>
                    <div
                      style={{
                        fontSize: 10,
                        color: '#9ca3af',
                        fontFamily: 'monospace',
                        flexShrink: 0,
                      }}
                    >
                      {timeStr}
                    </div>
                  </div>

                {diff !== null && (
                  <div
                    style={{
                      backgroundColor: isLate ? '#EF4444' : '#3B82F6',
                      color: 'white',
                      padding: '2px 8px',
                      borderRadius: 12,
                      fontSize: 10,
                      fontWeight: 600,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {diff === 0 ? 'On time' : isLate ? `Late by ${diff} mins` : `Early by ${Math.abs(diff)} mins`}
                  </div>
                )}
                </div>
              </div>
            );
          })}
          </div>

          {/* Grid content area */}
          <div
            style={{
              position: 'relative',
              marginLeft: TIME_COLUMN_WIDTH_PX,
              height: '100%',
            }}
          >

          {/* Horizontal grid lines (light gray) */}
          {timeMarkers.map(({ time, pixelPos }) => (
            <div
              key={`grid-${time}`}
              style={{
                position: 'absolute',
                top: pixelPos,
                left: 0,
                right: 0,
                height: 1,
                backgroundColor: '#f3f4f6',
              }}
            />
          ))}

          {/* Expected time dotted lines */}
          {sortedExpectedTimes.map(({ key, date, label }) => {
            const pixelPos = getPixelPositionForTime(date);
            const shouldDim =
              hoveredExpectedKey !== null &&
              hoveredExpectedKey !== key;
            
            return (
              <div
                key={key}
                style={{
                  position: 'absolute',
                  top: pixelPos,
                  left: 0,
                  right: 0,
                  display: 'flex',
                  alignItems: 'center',
                  // Keep expected lines above gridlines and connector lines,
                  // but below event cards unless hovered.
                  zIndex: hoveredExpectedKey === key ? 16 : 13,
                  transform: 'translateY(-50%)',
                  opacity: shouldDim ? 0.18 : 1,
                  transition: 'opacity 140ms ease',
                  cursor: 'pointer',
                }}
                onMouseEnter={() => onExpectedHoverEnter(key)}
                onMouseLeave={onExpectedHoverLeave}
              >
                {/* Dotted line */}
                <div
                  style={{
                    flex: 1,
                    height: 0,
                    borderTop: `2px dashed ${EXPECTED_NEUTRAL_LINE_COLOR}`,
                  }}
                />
              </div>
            );
          })}

          {/* Order status events as boxes - two columns: Order (left) and Collector (right) */}
          {historyEvents.map((event) => {
            const boxTop = eventBoxTopById[event.id] ?? event.pixelPos;
            const eventAnchorPos = event.pixelPos;
            // Order status on left column, Collector status 16px to the right of order column
            // Order column: starts at 10px, ends at ~45%
            // Collector column: starts at 45% + 16px gap
            // Two equal columns with a fixed gap between them.
            const boxLeftPx = event.isCollector
              ? `calc(50% + ${COLUMN_GAP_PX / 2}px)`
              : `${COLUMN_INNER_PADDING_PX}px`;
            const maxWidth = `calc(50% - ${COLUMN_GAP_PX / 2 + COLUMN_INNER_PADDING_PX}px)`;
            
            const bgColor = event.isCollector ? '#EEF2FF' : '#F0FDF4';
            const borderColor = event.isCollector ? '#818CF8' : '#86EFAC';
            const textColor = event.isCollector ? '#4338CA' : '#166534';
            
            // Determine if late (positive diff = happened after expected = late)
            const isLate = event.diffMinutes !== null && event.diffMinutes > 0;
            const diffColor = isLate ? '#EF4444' : '#3B82F6';

            const isHovered = hoverSource === 'event' && hoveredEventId === event.id;
            const isAssociatedWithHoveredExpected =
              hoverSource === 'expected' &&
              hoveredExpectedKey !== null &&
              event.expectedTimeKey === hoveredExpectedKey;

            const shouldDim =
              hoverSource === 'event'
                ? hoveredEventId !== null && hoveredEventId !== event.id
                : hoverSource === 'expected'
                  ? hoveredExpectedKey !== null && !isAssociatedWithHoveredExpected
                  : false;

            const eventOpacity = shouldDim ? 0.28 : 1;
            
            // Box dimensions - box top is at eventTop (no transform on box)
            const boxHeight = BOX_HEIGHT_PX;
            const boxVisualTop = boxTop;
            const boxVisualBottom = boxTop + boxHeight;
            
            // Calculate vertical line properties
            const hasExpectedTime = event.expectedPixelPos !== null && event.diffMinutes !== null;
            let lineTop = 0;
            let lineHeight = 0;
            
            if (hasExpectedTime) {
              const expectedTop = event.expectedPixelPos!;
              
              if (eventAnchorPos >= expectedTop) {
                // Event happened after expected (late) - line goes from expected line down to box top
                lineTop = expectedTop;
                lineHeight = Math.max(0, boxVisualTop - expectedTop);
              } else {
                // Event happened before expected (early) - line goes from box bottom up to expected line
                lineTop = boxVisualBottom;
                lineHeight = Math.max(0, expectedTop - boxVisualBottom);
              }
            }
            
            // Position for the vertical line - 20px into the box from the left
            // Box starts at 10px (order) or calc(45% + 16px) (collector)
            const lineLeftPosition = event.isCollector 
              ? `calc(50% + ${COLUMN_GAP_PX / 2 + 20}px)`  // (gap/2) + 20px offset into box
              : '30px';              // 10px + 20px offset into box
            
            return (
              <React.Fragment key={event.id}>
                {/* Vertical dotted line connecting event to expected time */}
                {hasExpectedTime && lineHeight > 0 && (
                  <div
                    style={{
                      position: 'absolute',
                      top: lineTop,
                      left: lineLeftPosition,
                      width: 2,
                      height: lineHeight,
                      backgroundImage: `repeating-linear-gradient(
                        to bottom,
                        ${diffColor} 0px,
                        ${diffColor} 4px,
                        transparent 4px,
                        transparent 8px
                      )`,
                      zIndex: 12,
                      opacity: eventOpacity,
                      transition: 'opacity 140ms ease',
                    }}
                  />
                )}
                
                {/* Event box */}
                <div
                  style={{
                    position: 'absolute',
                    top: boxTop,
                    left: boxLeftPx,
                    maxWidth,
                    backgroundColor: isLate ? '#FEF2F2' : '#F0F4FF',
                    borderRadius: 16,
                    padding: '6px 10px',
                    zIndex: 15,
                    boxShadow: isHovered ? '0 10px 22px rgba(0,0,0,0.18)' : '0 1px 3px rgba(0,0,0,0.08)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    cursor: 'pointer',
                    opacity: eventOpacity,
                    transform: isHovered ? 'translateY(-2px) scale(1.01)' : 'none',
                    transition: 'opacity 140ms ease, transform 140ms ease, box-shadow 140ms ease',
                  }}
                  onMouseEnter={() => {
                    setHoveredEventId(event.id);
                    setHoveredExpectedKey(event.expectedTimeKey);
                    setHoverSource('event');
                  }}
                  onMouseLeave={() => {
                    setHoveredEventId(null);
                    setHoveredExpectedKey(null);
                    setHoverSource(null);
                  }}
                >
                  {/* Icon circle */}
                  <div
                    style={{
                      width: 28,
                      height: 28,
                      borderRadius: '50%',
                      border: '2px solid #CBD5E1',
                      backgroundColor: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    {event.isCollector 
                      ? getIconForCollectorStatus(event.status)
                      : getIconForOrderStatus(event.status)
                    }
                  </div>
                  
                  {/* Text content */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Typography
                      variant="caption"
                      style={{
                        fontWeight: 500,
                        color: '#1F2937',
                        fontSize: 12,
                        lineHeight: 1.3,
                      }}
                    >
                      {event.label}
                    </Typography>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <Typography
                        variant="caption"
                        style={{
                          color: isLate ? '#EF4444' : '#3B82F6',
                          fontSize: 11,
                          fontWeight: 500,
                        }}
                      >
                        {event.datetime.toFormat('HH:mm')}
                      </Typography>
                      {/* Time difference badge */}
                      {event.diffMinutes !== null && (
                        <div
                          style={{
                            backgroundColor: isLate ? '#EF4444' : '#3B82F6',
                            color: 'white',
                            padding: '2px 8px',
                            borderRadius: 12,
                            fontSize: 10,
                            fontWeight: 600,
                          }}
                        >
                          {event.diffMinutes === 0
                            ? 'On time'
                            : isLate
                            ? `Late by ${event.diffMinutes} mins`
                            : `Early by ${Math.abs(event.diffMinutes)} mins`}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </React.Fragment>
            );
          })}
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * Connected wrapper that fetches latest expected order times on mount
 * and passes them down to the presentational ExpectedTimetable component.
 */
export const ExpectedTimetableConnected: React.FC<ExpectedTimetableConnectedProps> = ({
  orderId,
  orderHistory,
  timezone = 'Europe/Dublin',
  initialExpectedTimes,
}) => {
  const dispatch = useAppDispatch();

  const order = useAppSelector((s) => {
    const details = s.orders.orderDetails as any;
    if (details?.id === orderId) return details;
    return (s.orders.ordersToFilter as any[])?.find((o) => o?.id === orderId);
  });
  const collectorName = (order as any)?.collectorName as string | undefined;

  const expectedOrderTime = useAppSelector(
    (s) => s.orders.latestExpectedOrderTimeByOrderId?.[orderId]
  );
  const isFetching = useAppSelector(
    (s) => !!s.orders.isFetchingLatestExpectedOrderTimeByOrderId?.[orderId]
  );

  useEffect(() => {
    if (!orderId) return;
    // Avoid refetch loops: only fetch if we have never loaded this orderId yet.
    if (expectedOrderTime !== undefined) return;
    if (isFetching) return;
    dispatch(fetchLatestExpectedOrderTimeByOrderId(orderId));
  }, [dispatch, orderId, expectedOrderTime, isFetching]);

  const expectedTimes: ExpectedTimes | undefined = useMemo(() => {
    if (expectedOrderTime && typeof expectedOrderTime === 'object') {
      return expectedOrderTime as ExpectedTimes;
    }
    return initialExpectedTimes;
  }, [expectedOrderTime, initialExpectedTimes, orderId]);

  if (!expectedTimes) {
    // Keep rendering predictable (consumer can also pass initialExpectedTimes).
    return null;
  }

  return (
    <ExpectedTimetable
      expectedTimes={expectedTimes}
      orderHistory={orderHistory}
      timezone={timezone}
      collectorName={collectorName}
    />
  );
};

export default ExpectedTimetable;
